import { SaveDialogContent, SaveButton } from "@/components/SaveForm";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserRound, Plus, Pencil, Trash2 } from "lucide-react";

const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";
const EMPTY = { nome: "", funcao: "", setor: "", ativo: true };

export default function Collaborators() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/collaborators").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ nome: c.nome, funcao: c.funcao || "", setor: c.setor || "", ativo: c.ativo }); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome é obrigatório."); return; }
    try {
      if (editing) await api.put(`/collaborators/${editing.id}`, form);
      else await api.post("/collaborators", form);
      toast.success(editing ? "Colaborador atualizado." : "Colaborador cadastrado.");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (c) => { try { await api.put(`/collaborators/${c.id}`, { ativo: !c.ativo }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (c) => { if (!window.confirm(`Excluir o colaborador ${c.nome}?`)) return; try { await api.delete(`/collaborators/${c.id}`); toast.success("Colaborador excluído."); load(); } catch (e) { toast.error(apiErr(e)); } };

  if (list === null) return <Skeleton className="h-96 bg-slate-800" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50 flex items-center gap-2"><UserRound className="h-7 w-7 text-sky-400" /> Colaboradores</h1><p className="text-slate-400 text-sm">Técnicos e funcionários que retiram peças (sem login). Usados no campo "Retirado por".</p></div>
        <Button data-testid="btn-new-collaborator" onClick={openNew} className="bg-sky-700 hover:bg-sky-600"><Plus className="h-4 w-4 mr-1" /> Novo colaborador</Button>
      </div>
      <Card className="card-metal p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
              <th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">Função</th><th className="text-left px-4 py-3">Setor</th><th className="text-center px-4 py-3">Ativo</th><th className="text-right px-4 py-3">Ações</th>
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} data-testid={`collaborator-row-${c.id}`} className="border-t border-slate-800">
                  <td className="px-4 py-2.5 text-slate-100">{c.nome}</td>
                  <td className="px-4 py-2.5 text-slate-400">{c.funcao || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-400">{c.setor || "—"}</td>
                  <td className="px-4 py-2.5 text-center"><Switch checked={c.ativo} onCheckedChange={() => toggle(c)} data-testid={`collaborator-active-${c.id}`} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="text-slate-300 h-8" onClick={() => openEdit(c)} data-testid={`btn-edit-collaborator-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Editar</Button>
                    <Button size="sm" variant="ghost" className="text-rose-400 h-8" onClick={() => remove(c)} data-testid={`btn-delete-collaborator-${c.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Excluir</Button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Nenhum colaborador cadastrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar colaborador" : "Novo colaborador"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="input-collab-nome" className={inp} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
            <div><Label>Função</Label><Input data-testid="input-collab-funcao" className={inp} value={form.funcao} onChange={(e) => setForm({ ...form, funcao: e.target.value })} /></div>
            <div><Label>Setor</Label><Input data-testid="input-collab-setor" className={inp} value={form.setor} onChange={(e) => setForm({ ...form, setor: e.target.value })} /></div>
            <div className="flex items-center justify-between rounded-lg bg-slate-950/60 px-3 py-2"><Label>Ativo</Label><Switch data-testid="switch-collab-active" checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><SaveButton data-testid="btn-save-collaborator" onClick={save} className="bg-sky-700 hover:bg-sky-600">Salvar</SaveButton></DialogFooter>
        </SaveDialogContent>
      </Dialog>
    </div>
  );
}
